import { component } from "picoapp";
import { createFocusTrap, type FocusTrap } from "focus-trap";
import {
  compensateADVisibility,
  fetchGeolocationSuggestions,
} from "@tablescape/utilities";

const localStorageKey = "locationManuallySet";

const showModal = (node: HTMLElement, trap: FocusTrap) => {
  node.classList.remove("close");
  compensateADVisibility(node, true);
  trap.activate();
  window.maestro.fireTrackEvent("viewCountrySelectionModal", {
    URL: window.location.href,
    path: window.location.pathname,
  });
};

function dismissModal() {
  window.localStorage.setItem(localStorageKey, window.Shopify.country);
}

function closeModal(node: HTMLElement, trap: FocusTrap) {
  node.classList.add("close");
  compensateADVisibility(node, false);
  trap.deactivate();
}

const closeModalPermanently = (node: HTMLElement, trap: FocusTrap) => {
  dismissModal();
  closeModal(node, trap);
};

function handleEscKey(
  event: KeyboardEvent,
  node: HTMLElement,
  trap: FocusTrap
) {
  if (event.key !== "Escape") return;
  if (node.classList.contains("close")) return;

  event.preventDefault();
  event.stopPropagation();
  closeModalPermanently(node, trap);
  window.maestro.fireTrackEvent("clickCountrySelectionModal", {
    URL: window.location.href,
    path: window.location.pathname,
    "click info": "esc",
  });
}

export const countrySelector = component(async (node: HTMLElement) => {
  const trap = createFocusTrap(node, {
    allowOutsideClick: true,
    setReturnFocus: "nav[role=navigation] a[data-mtr-target=logo]",
    initialFocus: ".js-initial-focus",
  });
  const storeCountryCode = document.querySelector("html")?.dataset.countryCode;

  const closeButton = node.querySelector<HTMLButtonElement>(
    ".geolocation-modal-close"
  );

  if (closeButton) {
    closeButton.addEventListener("click", () => {
      closeModalPermanently(node, trap);
      window.maestro.fireTrackEvent("clickCountrySelectionModal", {
        URL: window.location.href,
        path: window.location.pathname,
        "click info": "X",
      });
    });
  }

  const overlay = node.querySelector<HTMLElement>(".geolocation-modal-overlay");
  if (overlay) {
    overlay.addEventListener("click", () => {
      closeModalPermanently(node, trap);
      window.maestro.fireTrackEvent("clickCountrySelectionModal", {
        URL: window.location.href,
        path: window.location.pathname,
        "click info": "outside",
      });
    });
  }

  const countryOptionButtons = node.querySelectorAll<HTMLElement>(
    "[data-country-code]"
  );

  if (countryOptionButtons) {
    [...countryOptionButtons].forEach((elem) => {
      elem.addEventListener("click", (event) => {
        const countryCode = (<HTMLElement>event.currentTarget)?.dataset
          .countryCode;
        if (countryCode === storeCountryCode) {
          event.preventDefault();
          dismissModal();
          closeModal(node, trap);
        }

        window.maestro.fireTrackEvent("clickCountrySelectionModal", {
          URL: window.location.href,
          path: window.location.pathname,
          "click info": countryCode,
        });
      });
    });
  }

  window.addEventListener("keydown", (event) => {
    handleEscKey(event, node, trap);
  });

  const hasSkipParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("modal:geolocation") === "false";
  };
  const modalDismissed = () => window.localStorage.getItem(localStorageKey);
  const customerLocationMatchesStore = async () => {
    const geolocationSuggestions = await fetchGeolocationSuggestions();
    if (!geolocationSuggestions?.detected_values?.country?.handle) return false;

    return (
      geolocationSuggestions?.detected_values.country.handle ===
      window.Shopify?.country
    );
  };

  const storeMatches = await customerLocationMatchesStore();
  if (hasSkipParam() || modalDismissed() || storeMatches) {
    return;
  }
  showModal(node, trap);
});
